import {
  Button,
  ConfirmDialog,
  Checkbox,
  Typography,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Slider,
} from '@tunasong/ui-lib'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { TunaPluginRenderPropertiesProps } from '@tunasong/plugin-lib'
import type { GroupedPoll, Poll, PollOption, PollOptionAudio, PollResponse } from '@tunasong/schemas'
import { isDatePoll, isGroupedPoll, isManyOfManyPoll, isPoll, pollTypes } from '@tunasong/schemas'
import type { FC } from 'react'
import React from 'react'
import { useCallback, useId, useLayoutEffect, useRef } from 'react'
import { getAnswerCounts } from './availability.js'
import { FindAudioDialog } from './find-audio-dialog.js'
import { GroupListEditor } from './group-list-editor.js'
import { addGroup, newGroup } from './groups.js'
import type { OptionRowHandlers } from './option-row.js'
import { OptionRow } from './option-row.js'
import { SlotListEditor } from './slot-list-editor.js'
import css from './properties.module.css'

export interface PropertiesProps extends TunaPluginRenderPropertiesProps<Poll> {
  className?: string
}

/**
 * Never splice. Responses are keyed by option index, so removing element 1 of [0,1,2]
 * re-points every stored answer for element 2 onto element 1 — silently, and the symptom
 * is someone turning up on the wrong night. The option stays as a tombstone and is
 * filtered out of every render.
 *
 * And never un-set `removed`: re-adding the same option appends a NEW index, so old
 * answers cannot reattach to a question that was asked again.
 */
const tombstone = (option: PollOption): PollOption => ({ ...option, removed: true })

/** Set an option's audio, or drop the field entirely when it is cleared. */
const withAudio = (option: PollOption, audio: PollOptionAudio | null): PollOption => {
  if (audio) {
    return { ...option, audio }
  }
  const { audio: _cleared, ...rest } = option
  return rest
}

export const Properties: FC<PropertiesProps> = props => {
  const { className, element, onChange } = props
  const quotaLabelId = useId()

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } = graphHooks.useEntityChildren<PollResponse>({
    parentId: element.id,
    filter: 'pollresponse',
  })

  const handleClick = useCallback(() => {
    if (!isPoll(element)) {
      return
    }
    const { pollOptions = [] } = element
    const updated = {
      ...element,
      pollOptions: [...pollOptions, { name: '' }],
    }
    onChange(updated)
  }, [element, onChange])

  /**
   * Row handlers are created once and read the latest poll and callback through this ref. Rebuilding them per
   * render changed every row's props on every keystroke, so all rows re-rendered (GH #1969); and a handler from
   * an earlier render wrote a whole option list from a stale poll, wiping other rows (GH #1966).
   */
  const answerCounts = getAnswerCounts(allResponses)
  const latest = useRef({ element, onChange, answerCounts })
  useLayoutEffect(() => {
    latest.current = { element, onChange, answerCounts }
  })

  /**
   * Every ref-based write goes through here: it builds on the latest poll and records what it wrote, so a second
   * write landing before the parent re-renders builds on the first instead of overwriting it. The next render
   * replaces the record with whatever the parent actually holds.
   */
  const commit = (write: (current: Poll) => Poll | undefined) => {
    const { element: current, onChange: change } = latest.current
    if (!isPoll(current)) {
      return
    }
    const next = write(current)
    if (!next) {
      return
    }
    latest.current = { ...latest.current, element: next }
    change(next)
  }

  /** Write one option of the latest poll. The other option objects keep their identity. */
  const updateOptionAt = (idx: number, update: (option: PollOption) => PollOption) =>
    commit(current => ({
      ...current,
      pollOptions: current.pollOptions.map((option, i) => (i === idx ? update(option) : option)),
    }))

  /** A group-level write from the group editor, skipped if the latest poll is somehow no longer grouped. */
  const updateGrouped = (write: (current: GroupedPoll) => Poll) =>
    commit(current => (isGroupedPoll(current) ? write(current) : undefined))

  /** The first group turns a plain poll grouped. Only a many-of-many poll can be grouped. */
  const convertToGrouped = () =>
    commit(current => (isManyOfManyPoll(current) ? addGroup(current, newGroup('Group 1')) : undefined))

  /** With answers in, grouping changes the question people answered — and it cannot be undone — so it asks. */
  const [isConfirmingGroups, setConfirmingGroups] = React.useState(false)
  const handleAddFirstGroup = () => {
    if (latest.current.answerCounts.size > 0) {
      setConfirmingGroups(true)
      return
    }
    convertToGrouped()
  }
  const handleConfirmGroups = () => {
    setConfirmingGroups(false)
    convertToGrouped()
  }

  /**
   * Renaming and removing are different risks. Renaming silently changes what people agreed
   * to, so it stays blocked. Removing is visible and discards cleanly, so it is allowed —
   * behind a confirm when anyone has answered, and immediately when nobody has. The common
   * case should not pay for the rare one.
   */
  const [pendingRemoval, setPendingRemoval] = React.useState<{ idx: number; label: string } | undefined>(undefined)

  /** The options "Find audio" is open for; null when closed. Mounting it fresh each time resets its choices. */
  const [findAudioFor, setFindAudioFor] = React.useState<number[] | null>(null)

  /** One write for everything applied, from the latest poll. Options left out of `assignments` are untouched. */
  const applyAudio = (assignments: Map<number, PollOptionAudio | null>) =>
    commit(current =>
      assignments.size === 0
        ? undefined
        : {
            ...current,
            pollOptions: current.pollOptions.map((option, i) =>
              assignments.has(i) ? withAudio(option, assignments.get(i) ?? null) : option
            ),
          }
    )

  const [rowHandlers] = React.useState<OptionRowHandlers>(() => ({
    /** Spread the existing option: `{ name }` alone dropped entityRef, start, end and removed. */
    rename: (idx, name) => updateOptionAt(idx, option => ({ ...option, name })),
    /** Linking spreads the option, so text typed before linking survives as the option's label. */
    relink: (idx, entityRef, name) =>
      updateOptionAt(idx, option => ({ ...option, entityRef, ...(name === undefined ? {} : { name }) })),
    remove: (idx, label) => {
      if ((latest.current.answerCounts.get(idx) ?? 0) > 0) {
        setPendingRemoval({ idx, label })
        return
      }
      updateOptionAt(idx, tombstone)
    },
    findAudio: idx => setFindAudioFor([idx]),
  }))

  const handleCancelRemoval = () => setPendingRemoval(undefined)
  const handleConfirmRemoval = () => {
    if (pendingRemoval) {
      updateOptionAt(pendingRemoval.idx, tombstone)
    }
    setPendingRemoval(undefined)
  }

  const handleChangeType = useCallback(
    (ev: unknown, pollType: string) => {
      const updated = { ...element, pollType: pollType as never } satisfies Poll
      onChange(updated)
    },
    [element, onChange]
  )

  const handleQuotaChange = useCallback(
    (ev: unknown, value: number | number[]) => {
      const responseQuota = typeof value === 'number' && value > 0 ? value : undefined
      const updated = { ...element, responseQuota }
      onChange(updated)
    },
    [element, onChange]
  )

  const handleRandomize = (ev: React.ChangeEvent<HTMLInputElement>) => {
    const updated = { ...element, randomizeOptions: ev.target.checked }
    onChange(updated)
  }

  if (!isPoll(element) || !hasLoadedChildren) {
    return null
  }
  const { pollOptions = [], pollType } = element

  /**
   * The panel used to be replaced wholesale by "Cannot edit poll with responses". That was a
   * crude way to make the splice above unreachable; now that removal is a tombstone, only the
   * options somebody has actually answered need freezing. Adding one is always safe, because
   * appending shifts no index.
   */
  const answerCount = (idx: number) => answerCounts.get(idx) ?? 0
  const linkedIndices = pollOptions.flatMap((option, idx) => (!option.removed && option.entityRef ? [idx] : []))
  const hasAnyAnswers = answerCounts.size > 0

  /**
   * Named by what the row shows, so an unlabelled linked option reads as its entity instead of an empty
   * string. Date polls never reach this dialog (they return the slot editor below), so it says "option". GH #1970
   */
  const removalCount = pendingRemoval ? answerCount(pendingRemoval.idx) : 0
  const removalLabel = pendingRemoval?.label ?? ''
  const removalText =
    removalCount === 1
      ? `1 person has already answered for ${removalLabel}. Their answers for this option will be discarded.`
      : `${removalCount} people have already answered for ${removalLabel}. Their answers for this option will be discarded.`

  /** A rehearsal poll authors slots, not text options. */
  if (isDatePoll(element)) {
    return <SlotListEditor element={element} answerCounts={answerCounts} onChange={onChange} />
  }

  return (
    <div className={[css.root, className].filter(Boolean).join(' ')}>
      {isGroupedPoll(element) ? null : (
        <FormControl>
          <RadioGroup aria-label="options" name="options" value={pollType ?? ''} onChange={handleChangeType}>
            {pollTypes.map(value => (
              <FormControlLabel key={value} value={value} control={<Radio />} label={value} />
            ))}
          </RadioGroup>
        </FormControl>
      )}

      {isManyOfManyPoll(element) && !isGroupedPoll(element) && (
        <>
          <FormLabel id={quotaLabelId} className={css.formLabel}>
            Vote quota
          </FormLabel>
          <div className={css.sliderPadding}>
            <Slider
              aria-labelledby={quotaLabelId}
              value={typeof element.responseQuota === 'number' ? element.responseQuota : 0}
              onChange={handleQuotaChange}
              min={0}
              max={element.pollOptions?.length}
            />
          </div>
        </>
      )}

      <FormLabel className={css.formLabel}>Options</FormLabel>
      <FormControlLabel
        control={<Checkbox checked={element?.randomizeOptions} onChange={handleRandomize} />}
        label="Randomize order of options in poll"
      />
      <Typography variant="caption" className={css.helpText}>
        Add options for users to vote on. You can give the option a name, or optionally link to an entity that
        represents the option (e.g., a song or audio clip). A linked option shows the entity name unless you give it a
        label.
      </Typography>

      {linkedIndices.length > 0 ? (
        <Button onClick={() => setFindAudioFor(linkedIndices)} className={css.createButton}>
          Find audio for options
        </Button>
      ) : null}

      {isGroupedPoll(element) ? (
        <GroupListEditor
          element={element}
          responses={allResponses}
          answerCounts={answerCounts}
          rowHandlers={rowHandlers}
          update={updateGrouped}
        />
      ) : (
        <>
          {pollOptions.map((option, idx) =>
            option.removed ? null : (
              <OptionRow key={idx} idx={idx} option={option} answerCount={answerCount(idx)} handlers={rowHandlers} />
            )
          )}
          {hasAnyAnswers ? (
            <Typography variant="caption" color="textSecondary" className={css.helpText}>
              Options people have already answered can&apos;t be renamed — remove and re-add instead.
            </Typography>
          ) : null}

          <Button onClick={handleClick} className={css.createButton}>
            Create option
          </Button>
          {isManyOfManyPoll(element) ? (
            <Button onClick={handleAddFirstGroup} className={css.createButton}>
              Add group
            </Button>
          ) : null}
        </>
      )}

      {findAudioFor ? (
        <FindAudioDialog
          poll={element}
          optionIndices={findAudioFor}
          open={true}
          onApply={applyAudio}
          onClose={() => setFindAudioFor(null)}
        />
      ) : null}

      <ConfirmDialog
        open={Boolean(pendingRemoval)}
        title="Remove this option?"
        text={removalText}
        okText="Remove option"
        onCancel={handleCancelRemoval}
        onConfirm={handleConfirmRemoval}
      />

      <ConfirmDialog
        open={isConfirmingGroups}
        title="Organise options into groups?"
        text="This can't be undone. Answers already given stay, but each group will ask for its own number of picks."
        okText="Add group"
        onCancel={() => setConfirmingGroups(false)}
        onConfirm={handleConfirmGroups}
      />
    </div>
  )
}

export default Properties
